/**
 * The measurements a phone order row is laid out to.
 *
 * The counterpart of `columnSpecs` for the narrow list: those are the desktop grid's, these are the
 * row's. Here rather than on the components that draw with them because each is read by more than one
 * of them — `OrderListRow` and the placeholders in `OrdersListSkeleton` — and a component that exports
 * a number its siblings lay themselves out against has quietly become their layout authority. Numbers
 * no one else reads stay in the component that uses them.
 */

/**
 * The height every row in the list is laid out to, real or placeholder.
 *
 * Three lines — the delivery method, the customer and the status — plus the row's padding, stated
 * rather than left to add itself up out of font metrics: `OrdersListSkeleton` lays its rows out to the
 * same measure, so a screenful of placeholders is exactly as tall as the orders that replace it and
 * the list does not resize when they land.
 *
 * A floor rather than a fixed height, because the left column can run to a fourth line — a preorder
 * marker, or the date the customer-orders view puts above the time — and a row that met one of those
 * should grow rather than clip. Every row without one is this tall exactly, which is the common case
 * and so what the list's rhythm is set by.
 */
export const ROW_HEIGHT = '84px';

/**
 * The left column: the time, the price beneath it, and whatever marker the order carries.
 *
 * Fixed rather than sized to its content, so the detail column starts at the same place in every row
 * and the list reads as two columns rather than a ragged pair. Wide enough for the longest thing that
 * can land in it — a four-figure price, or the customer-orders view's `D.M.YYYY` date.
 */
export const WHEN_COLUMN_WIDTH = 76;

/**
 * How far a row holds its text off the edge of the list's sheet.
 *
 * Shared because the skeleton's placeholder rows start where the real text will, and a row whose
 * padding disagreed with theirs would shift the whole list sideways as the orders landed.
 */
export const ROW_INLINE_PADDING = 12;

/**
 * The slot the disclosure chevron sits in at a row's right edge.
 *
 * Stated rather than left to the icon, so the detail column ends in the same place in every row — and
 * shared because the skeleton reserves the slot too. It draws nothing there, a chevron being a control
 * rather than content, but it has to spend the same width or its placeholder bars would be wider than
 * the text they stand in for.
 */
export const CHEVRON_WIDTH = 20;
